import React from 'react';
import styled from 'styled-components';
import Typography from '../Typography';
import Button from '../Button/Button';
import Modal from '../Modal/Modal';
import { useTranslation } from 'react-i18next';
import { theme } from '../../theme';
import WarningOutlineIcon from '../Icons/WarningOutlineIcon';

const CancelEventModal = ({
  isOpen,
  onClose,
  onConfirm,
  eventTitle,
  isLoading,
  cancellationType,
  attendeesCount,
  paidAttendeesCount
}) => {
  const { t } = useTranslation('manageEventOptions');

  const getModalContent = () => {
    switch (cancellationType) {
      case 'no_attendees':
        return {
          title: t('cancelEventModal.noAttendees.title'),
          confirmText: t('cancelEventModal.noAttendees.confirmText'),
        };
      case 'free_only':
        return {
          title: t('cancelEventModal.freeOnly.title'),
          description: t('cancelEventModal.freeOnly.description', { count: attendeesCount }),
          confirmText: t('cancelEventModal.freeOnly.confirmText'),
        };
      case 'paid_attendees':
        return {
          title: t('cancelEventModal.paidAttendees.title'),
          description: t('cancelEventModal.paidAttendees.description', {
            total: attendeesCount,
            paid: paidAttendeesCount
          }),
          infoTitle: t('cancelEventModal.paidAttendees.infoTitle'),
          infoDescription: t('cancelEventModal.paidAttendees.infoDescription'),
          confirmText: t('cancelEventModal.paidAttendees.confirmText'),
        };
      default:
        return {
          title: t('cancelEventModal.default.title'),
          confirmText: t('cancelEventModal.default.confirmText'),
        };
    }
  };

  const modalContent = getModalContent();

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="480px" title={modalContent.title}>
      <ModalContent>
        <ModalHeader>
          <Typography
            $variant="body-1-medium"
            $variantTablet="body-1-medium"
            $variantMobile="body-2-medium"
            color={theme.colors.defaultMain}
            as="p"
          >
            {t('cancelEventModal.explanation')}
          </Typography>
        </ModalHeader>

        {cancellationType === 'paid_attendees' && (
          <ModalBody>
            <WarningContainer>
              <WarningBox>
                <WarningMessage>
                  <WarningTitle>
                    <WarningOutlineIcon
                      height={20}
                      width={20}
                      fill={theme.colors.errorMain}
                    />
                    <Typography
                      $variant="body-1-semibold"
                      $variantTablet="body-1-semibold"
                      $variantMobile="body-2-semibold"
                      color={theme.colors.errorMain}
                      as="p"
                    >
                      {t('cancelEventModal.attentionTitle')}
                    </Typography>
                  </WarningTitle>
                  <Typography
                    $variant="body-2-medium"
                    $variantTablet="body-2-medium"
                    $variantMobile="body-2-medium"
                    color={theme.colors.defaultStrong}
                    as="p"
                  >
                    {modalContent.description}
                    {/* {t('cancelEventModal.paidAttendees.infoTitle')} */}
                  </Typography>
                </WarningMessage>
                <InfoNextStep>
                  <Typography
                    $variant="body-2-semibold"
                    $variantTablet="body-2-semibold"
                    $variantMobile="body-2-semibold"
                    color={theme.colors.defaultStrong}
                    as="p"
                  >
                    {modalContent.infoTitle}
                  </Typography>
                  <Typography
                    $variant="body-2-medium"
                    $variantTablet="body-2-medium"
                    $variantMobile="body-2-medium"
                    color={theme.colors.defaultStrong}
                    as="p"
                  >
                    {modalContent.infoDescription}
                  </Typography>
                </InfoNextStep>
              </WarningBox>
            </WarningContainer>
          </ModalBody>
        )}
        {cancellationType === 'free_only' && (
          <ModalBody>
            <WarningContainer>
              <InfoBox>
                <WarningMessage>
                  <WarningTitle>
                    <WarningOutlineIcon
                      height={20}
                      width={20}
                      fill={theme.colors.warningMain}
                    />
                    <Typography
                      $variant="body-1-semibold"
                      $variantTablet="body-1-semibold"
                      $variantMobile="body-2-semibold"
                      color={theme.colors.warningMain}
                      as="p"
                    >
                      {t('cancelEventModal.attentionTitle')}
                    </Typography>
                  </WarningTitle>

                  <Typography
                    $variant="body-2-medium"
                    $variantTablet="body-2-medium"
                    $variantMobile="body-2-medium"
                    color={theme.colors.defaultStrong}
                    as="p"
                  >
                    {modalContent.description}
                  </Typography>
                </WarningMessage>
              </InfoBox>
            </WarningContainer>
          </ModalBody>
        )}
      </ModalContent>
      <ModalActions>
        <Button
          $variant="ghost"
          size="l"
          sizeTablet="l"
          sizeMobile="m"
          onClick={onClose}
          disabled={isLoading}
        >
          {t('cancelEventModal.cancel')}
        </Button>
        <Button
          $variant={cancellationType === 'paid_attendees' ? 'default' : 'dangerOutline'}
          size="l"
          sizeTablet="l"
          sizeMobile="m"
          onClick={onConfirm}
          disabled={isLoading}
        >
          {isLoading ? t('cancelEventModal.processing') : modalContent.confirmText}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default CancelEventModal;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
  padding: ${({ theme }) => theme.sizing.sm};
`;

const ModalHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: ${({ theme }) => theme.sizing.sm};
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.md};
`;

const InfoBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
  border-radius: ${({ theme }) => theme.radius.xs};
  background-color: ${({ theme }) => theme.fill.warningAlphaMain16};
`;

const WarningBox = styled(InfoBox)`
  background-color: ${({ theme }) => theme.fill.elserrorAlphaMain8};
`;

const ModalActions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.sizing.sm};
  padding: ${({ theme }) => theme.sizing.sm};
  border-top: 1px solid ${({ theme }) => theme.border.defaultSubtle};
`;

const WarningContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const WarningTitle = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const InfoNextStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.sizing.xs};
  padding: 12px;
  border-top: 1px solid ${({ theme }) => theme.fill.inverseAlphaMain8};
`;

const WarningMessage = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
  padding: 12px;
`;